import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaSave, FaArrowRight, FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import TablaEntidad from '../util/tablas/TablaEntidad';
import NumericInput from './NumericInput';
import {
  CONCEPTOS,
  ITBIS_OPTIONS,
  FORMAS_PAGO,
  crearLineaVacia,
  obtenerDatosDeConcepto,
  validarFormatoNCF,
  autoRellenarNCF,
  detectarTipoNCF,
  calcularITBIS,
  calcularITBISRetenido,
  calcularISRPorTipo,
  calcularTotalFacturado,
  validarLinea,
  convertirMesANumero
} from './Funciones/FuncionesProcesoCostosGastos';

/**
 * Modal Mejorado para Agregar Múltiples Líneas - Flujo en 2 Pasos
 * Paso 1: Configuración común (cantidad, proveedor, concepto base, etc)
 * Paso 2: Tabla compacta con solo campos variables (día, NCF, monto, concepto, forma pago)
 */
export default function ModalBulkAddLineasMejorado({
  mostrar,
  onClose,
  onLineasAgregadas,
  periodoActual,
  destinoITBIS
}) {
  const [paso, setPaso] = useState(1); // 1 = Configuración, 2 = Tabla Variables
  
  // Paso 1: Configuración Común
  const [configuracion, setConfiguracion] = useState({
    cantidadLineas: 5,
    id_entidad_proveedor: null,
    nombre_proveedor: '',
    rnc_proveedor: '',
    concepto: '',
    tipo_gasto_606: '',
    tipo_factura: 'Servicios',
    itbis_porcentaje: 18,
    itbis_retenido_porcentaje: 0,
    tipo_retencion: '',
    forma_pago: '01',
    descripcion: '',
    diaInicial: 0
  });

  // Paso 2: Líneas con campos variables
  const [lineas, setLineas] = useState([]);

  // Modal proveedor
  const [mostrarModalProveedor, setMostrarModalProveedor] = useState(false);

  // Refs para navegación con teclado
  const inputRefs = useRef({});

  // Reset al abrir modal
  useEffect(() => {
    if (mostrar) {
      setPaso(1);
      setConfiguracion({
        cantidadLineas: 5,
        id_entidad_proveedor: null,
        nombre_proveedor: '',
        rnc_proveedor: '',
        concepto: '',
        tipo_gasto_606: '',
        tipo_factura: 'Servicios',
        itbis_porcentaje: 18,
        itbis_retenido_porcentaje: 0,
        tipo_retencion: '',
        forma_pago: '01',
        descripcion: '',
        diaInicial: 0
      });
      setLineas([]);
    }
  }, [mostrar]);

  // Cuando cambia el concepto en configuración, auto-rellenar campos relacionados
  useEffect(() => {
    if (configuracion.concepto) {
      const datos = obtenerDatosDeConcepto(configuracion.concepto);
      if (datos) {
        setConfiguracion(prev => ({
          ...prev,
          tipo_gasto_606: datos.tipo_gasto_606,
          tipo_factura: datos.tipo_factura,
          itbis_retenido_porcentaje: datos.itbis_retenido_porcentaje,
          tipo_retencion: datos.tipo_retencion
        }));
      }
    }
  }, [configuracion.concepto]);

  // Handler cambio configuración
  const handleCambioConfiguracion = (campo, valor) => {
    setConfiguracion(prev => ({ ...prev, [campo]: valor }));
  };

  // Handler proveedor seleccionado
  const handleProveedorSeleccionado = (proveedor) => {
    setConfiguracion(prev => ({
      ...prev,
      id_entidad_proveedor: proveedor.id_entidad,
      nombre_proveedor: `${proveedor.nombre} ${proveedor.apellido || ''}`.trim(),
      rnc_proveedor: proveedor.rnc || proveedor.cedula || ''
    }));
    setMostrarModalProveedor(false);
  };

  // Generar líneas base desde configuración
  const generarLineas = () => {
    const nuevasLineas = [];
    const mes = convertirMesANumero(periodoActual);
    
    for (let i = 0; i < configuracion.cantidadLineas; i++) {
      const lineaBase = {
        // Campos comunes (desde configuración)
        mes,
        id_entidad_proveedor: configuracion.id_entidad_proveedor,
        nombre_proveedor: configuracion.nombre_proveedor,
        rnc_proveedor: configuracion.rnc_proveedor,
        tipo_gasto_606: configuracion.tipo_gasto_606,
        tipo_factura: configuracion.tipo_factura,
        itbis_retenido_porcentaje: configuracion.itbis_retenido_porcentaje,
        tipo_retencion: configuracion.tipo_retencion,
        descripcion: configuracion.descripcion,
        
        // Campos variables (vacíos o con defaults)
        dia: '', // Siempre vacío, usuario lo llena manualmente
        ncf: '',
        ncf_modificado: '',
        tipo_ncf: '',
        concepto: configuracion.concepto, // Default pero editable
        forma_pago: configuracion.forma_pago, // Default pero editable
        itbis_porcentaje: configuracion.itbis_porcentaje, // Default pero editable por línea
        monto: 0,
        
        // Campos calculados (inicializados en 0)
        descuento: 0,
        monto_facturado: 0,
        itbis_facturado: 0,
        itbis_retenido: 0,
        isr_retenido: 0,
        impuesto_selectivo: 0,
        otros_impuestos: 0,
        propina_legal: 0,
        otros_cargos: 0,
        total_facturado: 0,
        itbis_proporcionalidad: 0,
        itbis_costo: 0,
        numero_cheque: '',
        fecha_cheque: '',
        banco: '',
        cuenta: ''
      };
      
      nuevasLineas.push(lineaBase);
    }
    
    setLineas(nuevasLineas);
    setPaso(2);
    
    // Focus en primer input después de un delay
    setTimeout(() => {
      inputRefs.current['0-dia']?.focus();
    }, 100);
  };

  // Validar paso 1
  const validarPaso1 = () => {
    if (configuracion.cantidadLineas < 1 || configuracion.cantidadLineas > 50) {
      toast.error('La cantidad de líneas debe estar entre 1 y 50');
      return false;
    }
    if (!configuracion.id_entidad_proveedor) {
      toast.error('Debe seleccionar un proveedor');
      return false;
    }
    if (!configuracion.concepto) {
      toast.error('Debe seleccionar un concepto base');
      return false;
    }
    return true;
  };

  // Avanzar a paso 2
  const irAPaso2 = () => {
    if (validarPaso1()) {
      generarLineas();
    }
  };

  // Actualizar campo de una línea
  const actualizarLinea = (index, campo, valor) => {
    setLineas(prev => {
      const nuevasLineas = [...prev];
      nuevasLineas[index] = { ...nuevasLineas[index], [campo]: valor };
      const linea = nuevasLineas[index];

      // Auto-detectar tipo NCF
      if (campo === 'ncf') {
        const tipoDetectado = detectarTipoNCF(valor);
        if (tipoDetectado) {
          linea.tipo_ncf = tipoDetectado;
        }
      }

      // Si cambia concepto, auto-rellenar campos relacionados
      if (campo === 'concepto') {
        const datos = obtenerDatosDeConcepto(valor);
        if (datos) {
          linea.tipo_gasto_606 = datos.tipo_gasto_606;
          linea.tipo_factura = datos.tipo_factura;
          linea.itbis_retenido_porcentaje = datos.itbis_retenido_porcentaje;
          linea.tipo_retencion = datos.tipo_retencion;
          
          if (datos.tipo_factura === 'Bienes') {
            linea.tipo_retencion = '';
            linea.isr_retenido = 0;
          }
        }
      }

      // Auto-calcular Monto Facturado
      if (campo === 'monto' || campo === 'descuento') {
        const monto = campo === 'monto' ? valor : linea.monto;
        const descuento = linea.descuento;
        linea.monto_facturado = parseFloat(
          ((parseFloat(monto) || 0) - (parseFloat(descuento) || 0)).toFixed(2)
        );
      }

      // Auto-calcular ITBIS Facturado (solo si no se está editando manualmente)
      if (campo === 'monto' || campo === 'descuento' || campo === 'itbis_porcentaje') {
        const monto = linea.monto;
        const descuento = linea.descuento;
        const porcentaje = campo === 'itbis_porcentaje' ? valor : linea.itbis_porcentaje;

        if (porcentaje && porcentaje !== 'exento' && porcentaje !== 0) {
          linea.itbis_facturado = calcularITBIS(monto, descuento, porcentaje);
        } else {
          linea.itbis_facturado = 0;
        }
      }

      // Auto-calcular ITBIS Retenido
      if (campo === 'monto' || campo === 'descuento' || campo === 'itbis_porcentaje' || campo === 'itbis_facturado') {
        const itbisFacturado = campo === 'itbis_facturado' ? valor : linea.itbis_facturado;
        linea.itbis_retenido = calcularITBISRetenido(
          itbisFacturado,
          linea.itbis_retenido_porcentaje
        );
      }

      // Auto-calcular ISR
      if (campo === 'monto' || campo === 'descuento' || campo === 'concepto' || campo === 'itbis_porcentaje' || campo === 'itbis_facturado') {
        linea.isr_retenido = calcularISRPorTipo(
          linea.monto,
          linea.descuento,
          linea.tipo_retencion,
          linea.tipo_factura
        );
      }

      // Distribuir ITBIS según destino (misma lógica que ProcesoCostosGastos)
      if (campo === 'monto' || campo === 'descuento' || campo === 'concepto' || campo === 'itbis_porcentaje' || campo === 'itbis_facturado') {
        if (destinoITBIS === 'costo') {
          linea.itbis_proporcionalidad = 0;
          linea.itbis_costo = linea.itbis_facturado;
        } else if (destinoITBIS === 'proporcionalidad') {
          if (linea.tipo_factura === 'Servicios') {
            linea.itbis_proporcionalidad = linea.itbis_facturado;
            linea.itbis_costo = 0;
          } else {
            linea.itbis_proporcionalidad = 0;
            linea.itbis_costo = 0;
          }
        } else if (destinoITBIS === 'adelantar') {
          linea.itbis_proporcionalidad = 0;
          linea.itbis_costo = 0;
        } else {
          linea.itbis_proporcionalidad = 0;
          linea.itbis_costo = 0;
        }
      }

      // Calcular Total Facturado
      if (campo === 'monto' || campo === 'descuento' || campo === 'itbis_porcentaje' || campo === 'itbis_facturado') {
        linea.total_facturado = calcularTotalFacturado(linea);
      }

      return nuevasLineas;
    });
  };

  // Agregar nueva línea vacía
  const agregarLineaVacia = () => {
    const mes = convertirMesANumero(periodoActual);
    const lineaVacia = {
      mes,
      id_entidad_proveedor: configuracion.id_entidad_proveedor,
      nombre_proveedor: configuracion.nombre_proveedor,
      rnc_proveedor: configuracion.rnc_proveedor,
      tipo_gasto_606: configuracion.tipo_gasto_606,
      tipo_factura: configuracion.tipo_factura,
      itbis_retenido_porcentaje: configuracion.itbis_retenido_porcentaje,
      tipo_retencion: configuracion.tipo_retencion,
      descripcion: configuracion.descripcion,
      dia: '',
      ncf: '',
      ncf_modificado: '',
      tipo_ncf: '',
      concepto: configuracion.concepto,
      forma_pago: configuracion.forma_pago,
      itbis_porcentaje: configuracion.itbis_porcentaje,
      monto: 0,
      descuento: 0,
      monto_facturado: 0,
      itbis_facturado: 0,
      itbis_retenido: 0,
      isr_retenido: 0,
      impuesto_selectivo: 0,
      otros_impuestos: 0,
      propina_legal: 0,
      otros_cargos: 0,
      total_facturado: 0,
      itbis_proporcionalidad: 0,
      itbis_costo: 0,
      numero_cheque: '',
      fecha_cheque: '',
      banco: '',
      cuenta: ''
    };
    
    setLineas(prev => [...prev, lineaVacia]);
    
    // Focus en el primer campo de la nueva línea
    setTimeout(() => {
      inputRefs.current[`${lineas.length}-dia`]?.focus();
    }, 100);
  };

  // Eliminar línea
  const eliminarLinea = (index) => {
    if (lineas.length === 1) {
      toast.warning('Debe haber al menos una línea');
      return;
    }
    setLineas(prev => prev.filter((_, i) => i !== index));
  };

  // Navegación con teclado
  const handleKeyDown = (e, index, campo) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      
      // Si es NCF y presiona Enter, auto-rellenar
      if (campo === 'ncf' && e.key === 'Enter') {
        const ncfActual = lineas[index].ncf;
        const ncfAutoRellenado = autoRellenarNCF(ncfActual);
        if (ncfAutoRellenado !== ncfActual) {
          actualizarLinea(index, 'ncf', ncfAutoRellenado);
        }
      }
      
      // Navegar al siguiente campo
      const campos = ['dia', 'ncf', 'monto', 'itbis_porcentaje', 'itbis_facturado', 'concepto', 'forma_pago', 'descripcion'];
      const campoIndex = campos.indexOf(campo);
      
      if (campoIndex < campos.length - 1) {
        // Siguiente campo en la misma fila
        const siguienteCampo = campos[campoIndex + 1];
        inputRefs.current[`${index}-${siguienteCampo}`]?.focus();
      } else if (index < lineas.length - 1) {
        // Primera columna de la siguiente fila
        inputRefs.current[`${index + 1}-dia`]?.focus();
      }
    }
  };

  // Calcular totales
  const calcularResumen = () => {
    const lineasValidas = lineas.filter(linea => {
      const validacion = validarLinea(linea);
      return validacion.isValid;
    });

    return {
      cantidadValidas: lineasValidas.length,
      cantidadTotal: lineas.length,
      totalFacturado: lineasValidas.reduce((sum, l) => sum + (parseFloat(l.total_facturado) || 0), 0),
      totalMonto: lineasValidas.reduce((sum, l) => sum + (parseFloat(l.monto) || 0), 0)
    };
  };

  const resumen = calcularResumen();

  // Agregar líneas
  const handleAgregarLineas = () => {
    const lineasValidas = lineas.filter(linea => {
      const validacion = validarLinea(linea);
      return validacion.isValid;
    });

    if (lineasValidas.length === 0) {
      toast.error('No hay líneas válidas para agregar. Complete al menos una línea con todos los campos requeridos.');
      return;
    }

    onLineasAgregadas(lineasValidas);
    onClose();

    toast.success(
      <div>
        <strong>✓ {lineasValidas.length} línea{lineasValidas.length !== 1 ? 's' : ''} agregada{lineasValidas.length !== 1 ? 's' : ''}</strong>
        <div className="text-sm mt-1">
          Total: RD$ {resumen.totalFacturado.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
        </div>
      </div>,
      { autoClose: 4000 }
    );
  };

  // Render Paso 1: Configuración
  const renderPaso1 = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">📋 Paso 1: Configuración Común</h3>
        <p className="text-xs text-blue-700">
          Configure los valores que se repetirán en todas las líneas. En el siguiente paso solo tendrá que ingresar: Día, NCF y Monto.
        </p>
      </div>

      {/* Cantidad de Líneas */}
      <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          ¿Cuántas líneas va a agregar? <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            value={configuracion.cantidadLineas}
            onChange={(e) => handleCambioConfiguracion('cantidadLineas', parseInt(e.target.value) || 1)}
            min="1"
            max="50"
            className="w-32 px-4 py-2 text-lg font-bold text-center border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">líneas (máximo 50)</span>
        </div>
      </div>

      {/* Proveedor */}
      <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Proveedor (común para todas) <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMostrarModalProveedor(true)}
            className="flex-1 px-4 py-2 text-left border-2 border-gray-300 rounded-lg hover:border-blue-400 focus:ring-2 focus:ring-blue-500 transition-all"
          >
            {configuracion.nombre_proveedor || 'Seleccionar proveedor...'}
          </button>
          {configuracion.nombre_proveedor && (
            <button
              onClick={() => handleCambioConfiguracion('nombre_proveedor', '')}
              className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FaTimes />
            </button>
          )}
        </div>
        {configuracion.rnc_proveedor && (
          <p className="text-xs text-gray-500 mt-2">RNC: {configuracion.rnc_proveedor}</p>
        )}
      </div>

      {/* Concepto Base */}
      <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Concepto (base, puede cambiarse por línea) <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          list="conceptos-config"
          value={CONCEPTOS.find(c => c.value === configuracion.concepto)?.label || configuracion.concepto || ''}
          onChange={(e) => {
            const encontrado = CONCEPTOS.find(c => 
              c.label.toLowerCase() === e.target.value.toLowerCase()
            );
            handleCambioConfiguracion('concepto', encontrado ? encontrado.value : e.target.value);
          }}
          className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Escriba o seleccione..."
        />
        <datalist id="conceptos-config">
          {CONCEPTOS.map(c => (
            <option key={c.value} value={c.label} />
          ))}
        </datalist>
        {configuracion.tipo_gasto_606 && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="text-gray-500">Tipo 606:</span>
            <span className="font-medium text-gray-700">{configuracion.tipo_gasto_606}</span>
            <span className="text-gray-500">•</span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              configuracion.tipo_factura === 'Bienes' ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'
            }`}>
              {configuracion.tipo_factura}
            </span>
          </div>
        )}
      </div>

      {/* Grid: Forma Pago, ITBIS %, Día Inicial */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Forma de Pago */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Forma de Pago (base)
          </label>
          <select
            value={configuracion.forma_pago}
            onChange={(e) => handleCambioConfiguracion('forma_pago', e.target.value)}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {FORMAS_PAGO.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* ITBIS % */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            ITBIS %
          </label>
          <select
            value={configuracion.itbis_porcentaje}
            onChange={(e) => {
              const val = e.target.value === 'exento' ? 'exento' : parseInt(e.target.value);
              handleCambioConfiguracion('itbis_porcentaje', val);
            }}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {ITBIS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Día Inicial */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Día Inicial (opcional)
          </label>
          <input
            type="number"
            value={configuracion.diaInicial}
            onChange={(e) => handleCambioConfiguracion('diaInicial', parseInt(e.target.value) || 0)}
            min="0"
            max="31"
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">0 = vacío, se incrementa automáticamente</p>
        </div>
      </div>

      {/* Botón Continuar */}
      <div className="flex justify-end pt-4 border-t">
        <button
          onClick={irAPaso2}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
        >
          Continuar al Paso 2
          <FaArrowRight />
        </button>
      </div>
    </div>
  );

  // Render Paso 2: Tabla Variables
  const renderPaso2 = () => (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-green-900 mb-1">✓ Paso 2: Ingresar Datos Variables</h3>
          <p className="text-xs text-green-700">
            Complete: Día, NCF, Monto, ITBIS%, Concepto y Forma de Pago para cada línea. Use Enter/Tab para navegar rápidamente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={agregarLineaVacia}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center gap-2 text-sm font-medium"
            title="Agregar línea adicional"
          >
            <FaPlus />
            Agregar Línea
          </button>
          <button
            onClick={() => setPaso(1)}
            className="px-4 py-2 bg-white border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-all flex items-center gap-2 text-sm font-medium"
          >
            <FaArrowLeft />
            Volver
          </button>
        </div>
      </div>

      {/* Tabla Compacta */}
      <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-1 py-2 text-center font-semibold text-gray-700 border-b-2 w-8">#</th>
                <th className="px-1 py-2 text-center font-semibold text-gray-700 border-b-2 w-16">Día</th>
                <th className="px-1 py-2 text-left font-semibold text-gray-700 border-b-2 w-32">
                  NCF <span className="text-red-500">*</span>
                </th>
                <th className="px-1 py-2 text-right font-semibold text-gray-700 border-b-2 w-24">
                  Monto <span className="text-red-500">*</span>
                </th>
                <th className="px-1 py-2 text-center font-semibold text-gray-700 border-b-2 w-16">ITBIS %</th>
                <th className="px-1 py-2 text-right font-semibold text-gray-700 border-b-2 w-24">ITBIS $</th>
                <th className="px-1 py-2 text-left font-semibold text-gray-700 border-b-2">
                  Concepto <span className="text-red-500">*</span>
                </th>
                <th className="px-1 py-2 text-left font-semibold text-gray-700 border-b-2 w-40">
                  Forma Pago <span className="text-red-500">*</span>
                </th>
                <th className="px-1 py-2 text-left font-semibold text-gray-700 border-b-2 w-36">Descripción</th>
                <th className="px-1 py-2 text-right font-semibold text-gray-700 border-b-2 w-28">Total</th>
                <th className="px-1 py-2 text-center font-semibold text-gray-700 border-b-2 w-12">✓</th>
                <th className="px-1 py-2 text-center font-semibold text-gray-700 border-b-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((linea, index) => {
                const validacion = validarLinea(linea);
                const ncfValidation = linea.ncf ? validarFormatoNCF(linea.ncf) : { isValid: null };
                
                return (
                  <tr 
                    key={index}
                    className={`border-b hover:bg-blue-50 transition-colors ${
                      validacion.isValid ? 'bg-green-50' : ''
                    }`}
                  >
                    {/* # */}
                    <td className="px-1 py-1 text-center font-medium text-gray-600 text-xs">
                      {index + 1}
                    </td>
                    
                    {/* Día */}
                    <td className="px-1 py-1">
                      <input
                        ref={el => inputRefs.current[`${index}-dia`] = el}
                        type="text"
                        value={linea.dia}
                        onChange={(e) => actualizarLinea(index, 'dia', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'dia')}
                        className="w-full px-1 py-1 text-center text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Día"
                      />
                    </td>
                    
                    {/* NCF */}
                    <td className="px-1 py-1">
                      <input
                        ref={el => inputRefs.current[`${index}-ncf`] = el}
                        type="text"
                        value={linea.ncf}
                        onChange={(e) => actualizarLinea(index, 'ncf', e.target.value)}
                        onBlur={(e) => {
                          const autoFilled = autoRellenarNCF(e.target.value);
                          if (autoFilled !== e.target.value) {
                            actualizarLinea(index, 'ncf', autoFilled);
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, index, 'ncf')}
                        maxLength="13"
                        className={`w-full px-1 py-1 text-xs border rounded focus:ring-2 ${
                          linea.ncf
                            ? ncfValidation.isValid
                              ? 'border-green-400 bg-green-50'
                              : 'border-red-400 bg-red-50'
                            : 'border-gray-300'
                        }`}
                        placeholder="B0113"
                      />
                    </td>
                    
                    {/* Monto */}
                    <td className="px-1 py-1">
                      <NumericInput
                        ref={el => inputRefs.current[`${index}-monto`] = el}
                        value={linea.monto}
                        onChange={(valor) => actualizarLinea(index, 'monto', valor)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'monto')}
                        step={0.01}
                        min={0}
                        decimals={2}
                        className="w-full px-1 py-1 text-right text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </td>
                    
                    {/* ITBIS % */}
                    <td className="px-1 py-1">
                      <select
                        ref={el => inputRefs.current[`${index}-itbis_porcentaje`] = el}
                        value={linea.itbis_porcentaje}
                        onChange={(e) => {
                          const val = e.target.value === 'exento' ? 'exento' : parseInt(e.target.value);
                          actualizarLinea(index, 'itbis_porcentaje', val);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, index, 'itbis_porcentaje')}
                        className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      >
                        {ITBIS_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    
                    {/* ITBIS Facturado (Monto) - Editable */}
                    <td className="px-1 py-1">
                      <NumericInput
                        ref={el => inputRefs.current[`${index}-itbis_facturado`] = el}
                        value={linea.itbis_facturado}
                        onChange={(valor) => actualizarLinea(index, 'itbis_facturado', valor)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'itbis_facturado')}
                        step={0.01}
                        min={0}
                        decimals={2}
                        className="w-full px-1 py-1 text-right text-xs border border-blue-300 bg-blue-50 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                        title="ITBIS Facturado - Auto-calculado pero editable"
                      />
                    </td>
                    
                    {/* Concepto */}
                    <td className="px-1 py-1">
                      <input
                        ref={el => inputRefs.current[`${index}-concepto`] = el}
                        type="text"
                        list={`conceptos-${index}`}
                        value={CONCEPTOS.find(c => c.value === linea.concepto)?.label || linea.concepto || ''}
                        onChange={(e) => {
                          const encontrado = CONCEPTOS.find(c => 
                            c.label.toLowerCase() === e.target.value.toLowerCase()
                          );
                          actualizarLinea(index, 'concepto', encontrado ? encontrado.value : e.target.value);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, index, 'concepto')}
                        className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="Concepto..."
                      />
                      <datalist id={`conceptos-${index}`}>
                        {CONCEPTOS.map(c => (
                          <option key={c.value} value={c.label} />
                        ))}
                      </datalist>
                    </td>
                    
                    {/* Forma Pago */}
                    <td className="px-1 py-1">
                      <select
                        ref={el => inputRefs.current[`${index}-forma_pago`] = el}
                        value={linea.forma_pago || ''}
                        onChange={(e) => actualizarLinea(index, 'forma_pago', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'forma_pago')}
                        className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      >
                        {FORMAS_PAGO.map(f => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                    </td>
                    
                    {/* Descripción */}
                    <td className="px-1 py-1">
                      <input
                        ref={el => inputRefs.current[`${index}-descripcion`] = el}
                        type="text"
                        value={linea.descripcion || ''}
                        onChange={(e) => actualizarLinea(index, 'descripcion', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'descripcion')}
                        className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Descripción..."
                      />
                    </td>
                    
                    {/* Total */}
                    <td className="px-1 py-1 text-right">
                      <span className="text-xs font-semibold text-green-600">
                        {(linea.total_facturado || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    
                    {/* Estado */}
                    <td className="px-1 py-1 text-center">
                      {validacion.isValid ? (
                        <span className="text-green-600 font-bold text-base">✓</span>
                      ) : (
                        <span className="text-red-500 font-bold text-base">✗</span>
                      )}
                    </td>
                    
                    {/* Acciones */}
                    <td className="px-1 py-1 text-center">
                      <button
                        onClick={() => eliminarLinea(index)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Eliminar línea"
                      >
                        <FaTrash size={10} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumen y Botones */}
      <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="space-y-1">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-green-600">{resumen.cantidadValidas}</span> de {resumen.cantidadTotal} líneas válidas
            </div>
            <div className="text-xs text-gray-500">
              {resumen.cantidadTotal - resumen.cantidadValidas > 0 && (
                <span className="text-orange-600">
                  {resumen.cantidadTotal - resumen.cantidadValidas} con errores
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Total a agregar:</div>
            <div className="text-2xl font-bold text-green-600">
              RD$ {resumen.totalFacturado.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all flex items-center gap-2"
          >
            <FaTimes />
            Cancelar
          </button>
          <button
            onClick={handleAgregarLineas}
            disabled={resumen.cantidadValidas === 0}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
          >
            <FaSave />
            Agregar {resumen.cantidadValidas} Línea{resumen.cantidadValidas !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );

  // Modal personalizado extra ancho
  if (!mostrar) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[50] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        {/* Modal Container */}
        <div className="bg-white rounded-xl shadow-2xl w-[92vw] max-h-[92vh] overflow-hidden flex flex-col">
          {/* Header con botón cerrar */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <h2 className="text-xl font-bold text-gray-800">Agregar Múltiples Líneas - Modo Rápido</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              title="Cerrar"
            >
              <FaTimes size={20} className="text-gray-600" />
            </button>
          </div>

          {/* Content scrollable */}
          <div className="overflow-y-auto flex-1 p-4">
            {/* Breadcrumb de pasos */}
            <div className="flex items-center gap-2 mb-6">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                paso === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                Paso 1: Configuración
              </div>
              <div className="text-gray-400">→</div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                paso === 2 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                Paso 2: Datos Variables
              </div>
            </div>

            {/* Content */}
            {paso === 1 ? renderPaso1() : renderPaso2()}
          </div>
        </div>
      </div>

      {/* Modal Proveedor - Overlay separado */}
      {mostrarModalProveedor && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setMostrarModalProveedor(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h3 className="text-lg font-bold text-gray-800">Seleccione un proveedor</h3>
              <button
                onClick={() => setMostrarModalProveedor(false)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <FaTimes size={18} />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <TablaEntidad
                tipoEntidad={2}
                descripcion="Seleccione un proveedor"
                setEntidadSeleccionada={handleProveedorSeleccionado}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

